
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface PasswordHistory {
  id: string;
  user_id: string;
  changed_at: string;
  method: string;
  ip_address?: string;
  user_agent?: string;
}

export interface TwoFactorAuth {
  id: string;
  user_id: string;
  method: 'totp' | 'sms' | 'email';
  secret?: string;
  backup_codes?: string[];
  verified: boolean;
  created_at: string;
  last_used?: string;
}

export interface LoginAttempt {
  id: string;
  user_id?: string;
  email?: string;
  success: boolean;
  attempted_at: string;
  ip_address: string;
  user_agent?: string;
  failure_reason?: string;
  location_info?: Record<string, any>;
}

export const useSecurity = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch password history
  const {
    data: passwordHistory = [],
    isLoading: isLoadingPasswordHistory
  } = useQuery({
    queryKey: ['password-history'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await supabase
        .from('password_history')
        .select('*')
        .eq('user_id', user.id)
        .order('changed_at', { ascending: false })
        .limit(10);
      
      if (error) throw error;
      return data as PasswordHistory[];
    },
  });

  // Fetch login attempts
  const {
    data: loginAttempts = [],
    isLoading: isLoadingAttempts
  } = useQuery({
    queryKey: ['login-attempts'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await supabase
        .from('login_attempts')
        .select('*')
        .eq('user_id', user.id)
        .order('attempted_at', { ascending: false })
        .limit(20);
      
      if (error) throw error;
      return data as LoginAttempt[];
    },
  });

  // Fetch 2FA settings
  const {
    data: twoFactorAuth = [],
    isLoading: isLoadingTwoFactor
  } = useQuery({
    queryKey: ['two-factor-auth'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await supabase
        .from('user_two_factor')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at');
      
      if (error) throw error;
      return data as TwoFactorAuth[];
    },
  });

  // Change password mutation
  const changePasswordMutation = useMutation({
    mutationFn: async ({ currentPassword, newPassword }: { currentPassword: string; newPassword: string }) => {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['password-history'] });
      toast({
        title: 'Success',
        description: 'Password changed successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: 'Failed to change password',
        variant: 'destructive',
      });
    },
  });

  // Enable 2FA mutation
  const enable2FAMutation = useMutation({
    mutationFn: async ({ method, secret }: { method: 'totp' | 'sms' | 'email'; secret?: string }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { error } = await supabase
        .from('user_two_factor')
        .insert({
          user_id: user.id,
          method,
          secret,
          verified: false
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['two-factor-auth'] });
      toast({
        title: 'Success',
        description: '2FA method added successfully',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: 'Failed to enable 2FA',
        variant: 'destructive',
      });
    },
  });

  // Disable 2FA mutation
  const disable2FAMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('user_two_factor')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['two-factor-auth'] });
      toast({
        title: 'Success',
        description: '2FA method removed successfully',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: 'Failed to disable 2FA',
        variant: 'destructive',
      });
    },
  });

  // Toggle 2FA mutation
  const toggleTwoFactorMutation = useMutation({
    mutationFn: async ({ method, enable }: { method: string; enable: boolean }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      if (enable) {
        const { error } = await supabase
          .from('user_two_factor')
          .insert({
            user_id: user.id,
            method,
            verified: false
          });
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('user_two_factor')
          .delete()
          .eq('user_id', user.id)
          .eq('method', method);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['two-factor-auth'] });
      toast({
        title: 'Success',
        description: '2FA settings updated successfully',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: 'Failed to update 2FA settings',
        variant: 'destructive',
      });
    },
  });

  return {
    passwordHistory,
    twoFactorAuth,
    loginAttempts,
    twoFactorMethods: twoFactorAuth,
    isLoadingPasswordHistory,
    isLoadingHistory: isLoadingPasswordHistory,
    isLoadingTwoFactor,
    isLoadingAttempts,
    enable2FA: enable2FAMutation.mutate,
    disable2FA: disable2FAMutation.mutate,
    changePassword: changePasswordMutation.mutate,
    toggleTwoFactor: toggleTwoFactorMutation.mutate,
    isEnabling2FA: enable2FAMutation.isPending,
    isDisabling2FA: disable2FAMutation.isPending,
    isChangingPassword: changePasswordMutation.isPending,
    isTogglingTwoFactor: toggleTwoFactorMutation.isPending,
  };
};
